import { motion } from 'framer-motion';
import { Package, Plus, Edit2, Trash2, Ruler, Weight, Eye, Box, Plane, Ship, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const getModeIcon = (mode) => {
  const icons = { air: Plane, ocean: Ship, road: Truck };
  return icons[mode] || Box;
};

const getModeColor = (mode) => {
  const colors = {
    air: 'bg-sky-100 text-sky-800 border-sky-300',
    ocean: 'bg-blue-100 text-blue-800 border-blue-300',
    road: 'bg-amber-100 text-amber-800 border-amber-300',
  };
  return colors[mode] || 'bg-steel-100 text-steel-800 border-steel-300';
};

const EquipmentCard = ({ item, onEdit, onDelete, onViewDetails }) => {
  const ModeIcon = getModeIcon(item.mode);
  
  return (
    <Card className="h-full hover:shadow-lg transition-shadow">
      <div className="relative h-40 overflow-hidden rounded-t-lg bg-steel-100">
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy-100 to-steel-200">
            <ModeIcon className="w-16 h-16 text-navy-300" />
          </div>
        )}
        <Badge className={`absolute top-3 right-3 ${getModeColor(item.mode)}`}>
          {item.mode}
        </Badge>
      </div>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-semibold text-navy-800">{item.name}</h3>
            <p className="text-sm text-steel-500">{item.code}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-steel-600 mb-2">
          <Badge variant="outline">{item.category}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-steel-500 mb-4">
          {item.dimensions && (
            <div className="flex items-center gap-1">
              <Ruler className="w-3 h-3" />
              <span>{item.dimensions.length_cm}×{item.dimensions.width_cm}×{item.dimensions.height_cm}</span>
            </div>
          )}
          {item.capacity && (
            <div className="flex items-center gap-1">
              <Weight className="w-3 h-3" />
              <span>{(item.capacity.max_weight_kg / 1000).toFixed(1)}t</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => onViewDetails(item)}>
            <Eye className="w-4 h-4 mr-1" /> View
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit(item)}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => onDelete(item)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const EquipmentGrid = ({ 
  equipment, 
  loading, 
  onOpenCreate, 
  onEdit, 
  onDelete, 
  onViewDetails,
  t
}) => {
  if (loading) {
    return (
      <div className="col-span-full flex items-center justify-center py-12">
        <Package className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (equipment.length === 0) {
    return (
      <div className="col-span-full text-center py-12">
        <Package className="w-12 h-12 text-steel-300 mx-auto mb-4" />
        <p className="text-steel-500">{t('equipmentMgmt.noEquipment')}</p>
        <Button onClick={onOpenCreate} className="mt-4">
          <Plus className="w-4 h-4 mr-2" />
          {t('equipmentMgmt.addEquipment')}
        </Button>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {equipment.map((item, index) => (
        <motion.div
          key={item.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.03 }}
        >
          <EquipmentCard
            item={item}
            onEdit={onEdit}
            onDelete={onDelete}
            onViewDetails={onViewDetails}
          />
        </motion.div>
      ))}
    </div>
  );
};

export { EquipmentGrid, EquipmentCard, getModeIcon, getModeColor };
export default EquipmentGrid;
